import { Location } from "@angular/common";
import { Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from "@angular/core";
import { NavigationExtras } from "@angular/router";
import { Store } from "@ngrx/store";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { of, Subscription } from "rxjs";
import { filter, switchMap } from "rxjs/operators";

import { RouteLink, RouteLinkParams } from "../route-link/route-link.model";
import { RouteLinkService } from "../route-link/route-link.service";
import { extractRouteLink } from "../route-link/route-link.util";
import { RouterState } from "../state";
import { RouteSelector } from "../store/route.selector";
import { RouteService } from "../store/route.service";

import { RouteLinkActiveConfigService } from "./route-link-active.config";
import { RouteLinkActiveOptions } from "./route-link-active.model";

@Directive({
	selector: "[obgRouteLinkActive]",
})
export class RouteLinkActiveDirective implements OnChanges, OnDestroy {
	@Input() obgRouteLinkActive: string | RouteLink | undefined;
	@Input() obgRouteLinkActiveOptions: RouteLinkActiveOptions | undefined;
	@Input() obgRouteLink: string | RouteLinkParams | undefined;
	@Input() obgRouteLinkOptions: NavigationExtras | undefined;
	@Input() href: string | undefined;

	private isActive$$: Subscription | undefined;
	private logger: Log;
	private routeLinkOptions = this.config.getOptions();

	constructor(
		private el: ElementRef,
		private renderer: Renderer2,
		private routeService: RouteService,
		private config: RouteLinkActiveConfigService,
		private routeLinkService: RouteLinkService,
		private store: Store<RouterState>,
		private location: Location,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("routeLinkActiveDirective");
	}

	ngOnChanges(changes: SimpleChanges) {
		if (this.isActive$$) {
			this.isActive$$.unsubscribe();
			this.isActive$$ = undefined;
		}
		if (changes.obgRouteLinkActiveOptions) {
			this.routeLinkOptions = { ...this.config.getOptions(), ...this.obgRouteLinkActiveOptions };
		}

		let routeLink: RouteLink | undefined;

		if (this.obgRouteLinkActive) {
			routeLink = extractRouteLink(this.obgRouteLinkActive);
		} else if (this.obgRouteLink) {
			routeLink = extractRouteLink(this.obgRouteLink);
		} else if (this.href) {
			routeLink = { url: this.href };
		}

		if (!routeLink || !(routeLink.route || routeLink.url)) {
			this.logger.warn(
				"ngOnChanges",
				"No params are provided. Either [obgRouteLinkActive] or [obgRouteLink] or [href] must be provided."
			);
			return;
		}

		const url$ = routeLink.route
			? this.routeService.generateUrl$({
					routeName: routeLink.route,
					params: routeLink.params,
					extras: this.obgRouteLinkOptions,
				})
			: of(this.location.prepareExternalUrl(this.routeLinkService.removeBase(routeLink.url!)));

		this.isActive$$ = url$
			.pipe(
				switchMap((url) =>
					this.routeLinkOptions.urlsToMatchFrom
						? this.store.select(
								RouteSelector.isActiveByUrl(
									url,
									this.routeLinkOptions.urlsToMatchFrom,
									this.routeLinkOptions.ignoreQueryParams
								)
							)
						: this.store.select(
								RouteSelector.isActiveByUrl(
									url,
									this.routeLinkOptions.matchExact,
									this.routeLinkOptions.ignoreQueryParams
								)
							)
				),
				filter(() => !!this.routeLinkOptions.cssClass)
			)
			.subscribe((isActive) =>
				isActive
					? this.renderer.addClass(this.el.nativeElement, this.routeLinkOptions.cssClass!)
					: this.renderer.removeClass(this.el.nativeElement, this.routeLinkOptions.cssClass!)
			);
	}

	ngOnDestroy() {
		if (this.isActive$$) {
			this.isActive$$.unsubscribe();
		}
	}
}
